import { useCallback, useEffect, useState } from 'react';
import { ROLE_LABEL_TH, type Role } from '@foundation/shared';
import { ApiRequestError, api, tokenStore } from '../lib/api';
import { getIdToken, initLiff, isLoggedIn, login as liffLogin } from '../lib/liff';
import { useAuth } from '../auth';

interface InvitePreview {
  valid: boolean;
  staffCode: string;
  fullName: string;
  role: Role;
  expiresAt: string;
}

type State =
  | { kind: 'loading' }
  | { kind: 'invalid'; message: string }
  | { kind: 'preview'; invite: InvitePreview }
  | { kind: 'linking' }
  | { kind: 'done'; name: string };

/**
 * หน้าผูกบัญชี LINE — เปิดจากลิงก์ที่ผู้ดูแลระบบส่งให้
 * URL: https://liff.line.me/{LIFF_ID}/link?t=<token>
 */
export default function LinkAccount() {
  const { retry } = useAuth();
  const [state, setState] = useState<State>({ kind: 'loading' });

  const token = new URLSearchParams(window.location.search).get('t') ?? '';

  const preview = useCallback(async () => {
    if (!token) {
      setState({ kind: 'invalid', message: 'ลิงก์ไม่ถูกต้อง — ไม่พบรหัสเชิญ' });
      return;
    }
    try {
      await initLiff();
      if (!isLoggedIn()) {
        liffLogin();
        return;
      }
      const invite = await api<InvitePreview>(`/auth/invite/${encodeURIComponent(token)}`, {
        anonymous: true,
      });
      setState({ kind: 'preview', invite });
    } catch (err) {
      setState({
        kind: 'invalid',
        message: err instanceof ApiRequestError ? err.message : 'ตรวจสอบลิงก์ไม่สำเร็จ',
      });
    }
  }, [token]);

  useEffect(() => {
    void preview();
  }, [preview]);

  async function doLink() {
    setState({ kind: 'linking' });
    try {
      const idToken = getIdToken();
      if (!idToken) throw new ApiRequestError('LINE_VERIFY_FAILED', 401);

      const res = await api<{ token: string; profile: { name: string } }>('/auth/link', {
        method: 'POST',
        anonymous: true,
        body: { idToken, token },
      });
      tokenStore.set(res.token);
      setState({ kind: 'done', name: res.profile.name });
    } catch (err) {
      setState({
        kind: 'invalid',
        message: err instanceof ApiRequestError ? err.message : 'ผูกบัญชีไม่สำเร็จ',
      });
    }
  }

  if (state.kind === 'loading') {
    return <div className="center">กำลังตรวจสอบลิงก์…</div>;
  }

  if (state.kind === 'invalid') {
    return (
      <div className="screen">
        <div className="alert error">{state.message}</div>
        <p className="muted small">
          กรุณาติดต่อผู้ดูแลระบบเพื่อขอลิงก์ผูกบัญชีใหม่ — ลิงก์แต่ละอันใช้ได้ครั้งเดียวและมีอายุ 7 วัน
        </p>
      </div>
    );
  }

  if (state.kind === 'done') {
    return (
      <div className="splash">
        <div style={{ fontSize: 44 }}>✅</div>
        <h1 style={{ fontSize: 20, margin: 0 }}>ผูกบัญชีเรียบร้อย</h1>
        <p className="muted">
          ยินดีต้อนรับคุณ{state.name}
          <br />
          กดเมนูด้านล่างในแชทเพื่อเริ่มใช้งานได้เลย
        </p>
        <button className="block" onClick={retry}>
          เข้าสู่หน้างาน
        </button>
      </div>
    );
  }

  const busy = state.kind === 'linking';
  const invite = state.kind === 'preview' ? state.invite : null;

  return (
    <div className="screen">
      <div className="topbar">
        <h1>ผูกบัญชีเจ้าหน้าที่</h1>
      </div>

      <div className="card">
        <h2>ยืนยันตัวตน</h2>
        <dl className="detail-list">
          <dt>ชื่อ-สกุล</dt>
          <dd>{invite?.fullName}</dd>
          <dt>รหัสเจ้าหน้าที่</dt>
          <dd className="mono">{invite?.staffCode}</dd>
          <dt>สิทธิ์</dt>
          <dd>{invite ? ROLE_LABEL_TH[invite.role] : ''}</dd>
        </dl>
      </div>

      <div className="alert info small">
        เมื่อกดยืนยัน บัญชี LINE ที่คุณใช้อยู่ตอนนี้จะถูกผูกกับเจ้าหน้าที่คนนี้อย่างถาวร
        หากข้อมูลด้านบนไม่ใช่คุณ กรุณาอย่ากดยืนยันและติดต่อผู้ดูแลระบบ
      </div>

      <button className="block" onClick={() => void doLink()} disabled={busy}>
        {busy ? 'กำลังผูกบัญชี…' : 'ยืนยันว่าเป็นฉัน'}
      </button>
    </div>
  );
}
